import React, { useState } from 'react';
import {
  ShoppingBag,
  Trash2,
  Plus,
  Minus,
  Share2,
  Bookmark,
  Users,
  ArrowRight,
  Check,
  X,
  Tag,
  Pencil,
  UserRound,
} from 'lucide-react';
export const CartSidebar = ({
  items,
  onUpdateQuantity,
  onRemoveItem,
  onEditItem,
  onCheckout,
  onSaveForLater,
  onCreateGroupOrder,
  isSavingForLater = false,
  isCreatingGroupOrder = false,
  isDrawer = false,
  isOpen = false,
  onCloseDrawer,
  appliedPromo = '',
  onApplyPromo,
  discountAmount = 0,
  deliveryFee: realDeliveryFee = 0,
  taxRate: realTaxRate = 0,
  disabled = false,
}) => {
  const [promoInput, setPromoInput] = useState('');
  const [promoMessage, setPromoMessage] = useState(null);
  const [copiedShare, setCopiedShare] = useState(false);
  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const deliveryFee = subtotal > 0 ? realDeliveryFee : 0;
  const taxRate = realTaxRate;
  const tax = subtotal * taxRate;
  const total = Math.max(0, subtotal - discountAmount + deliveryFee + tax);
  const handleApplyPromo = () => {
    if (!promoInput.trim()) return;
    if (onApplyPromo) {
      const success = onApplyPromo(promoInput.trim().toUpperCase());
      if (success) {
        setPromoMessage({
          type: 'success',
          text: 'Panda promo applied: $5.00 off!',
        });
      } else {
        setPromoMessage({
          type: 'error',
          text: 'Invalid code. Try "PANDA10" or "HAPPYPANDA"',
        });
      }
    }
  };
  const handleShareCart = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).catch(() => {});
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    }
  };
  const content = React.createElement(
    'div',
    {
      className:
        'flex flex-col h-full bg-white rounded-2xl md:rounded-3xl border border-gray-200/80 shadow-sm overflow-hidden',
    },
    React.createElement(
      'div',
      {
        className:
          'p-4 sm:p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-panda-tint/50 to-white',
      },
      React.createElement(
        'div',
        { className: 'flex items-center gap-2' },
        React.createElement(
          'h3',
          { className: 'font-heading font-bold text-lg text-[#202124]' },
          'Order Details',
        ),
        items.length > 0 &&
          React.createElement(
            'span',
            {
              className:
                'w-6 h-6 rounded-full bg-[#E53935] text-white text-xs font-bold flex items-center justify-center',
            },
            items.reduce((s, i) => s + i.quantity, 0),
          ),
      ),
      isDrawer &&
        onCloseDrawer &&
        React.createElement(
          'button',
          {
            onClick: onCloseDrawer,
            className:
              'w-8 h-8 rounded-full bg-gray-100 text-gray-600 hover:bg-[#E53935] hover:text-white flex items-center justify-center transition-colors',
          },
          React.createElement(X, { className: 'w-4 h-4' }),
        ),
    ),
    React.createElement(
      'div',
      { className: 'flex-1 overflow-y-auto p-4 sm:p-5' },
      items.length === 0
        ? React.createElement(
            'div',
            {
              className:
                'py-12 px-4 text-center flex flex-col items-center justify-center',
            },
            React.createElement(
              'div',
              {
                className:
                  'w-20 h-20 rounded-2xl bg-panda-tint border-2 border-dashed border-red-200 flex items-center justify-center mb-4 text-[#E53935] animate-pulse',
              },
              React.createElement(ShoppingBag, {
                className: 'w-10 h-10 stroke-1',
              }),
            ),
            React.createElement(
              'h4',
              {
                className:
                  'font-heading font-bold text-base text-[#202124] mb-1',
              },
              'Your cart is empty!',
            ),
            React.createElement(
              'p',
              { className: 'text-xs text-gray-500 max-w-[200px] mb-4' },
              'Add mouthwatering dishes to get started with Panda delivery.',
            ),
            React.createElement(
              'div',
              {
                className:
                  'text-[11px] bg-panda-tint text-panda-dark px-3 py-1.5 rounded-full font-medium',
              },
              '\uD83D\uDCA1 Tip: Try Chicken Teriyaki Bento!',
            ),
          )
        : React.createElement(
            'div',
            { className: 'space-y-4 divide-y divide-gray-100' },
            items.map(item => {
              const breakdown = item.optionBreakdown || [];
              const isMultiTier = breakdown.length > 1;
              return React.createElement(
                'div',
                { key: item.id, className: 'pt-3 first:pt-0' },
                React.createElement(
                  'div',
                  { className: 'flex items-start justify-between gap-3' },
                  React.createElement(
                    'div',
                    { className: 'flex items-start gap-2.5 min-w-0' },
                    React.createElement(
                      'span',
                      {
                        className:
                          'w-5 h-5 rounded-full bg-panda-tint text-[#E53935] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5',
                      },
                      item.quantity,
                    ),
                    React.createElement(
                      'div',
                      { className: 'min-w-0' },
                      React.createElement(
                        'h4',
                        {
                          className:
                            'font-heading font-semibold text-sm text-[#202124] leading-snug',
                        },
                        item.name,
                      ),
                      breakdown.length > 0 &&
                        React.createElement(
                          'div',
                          { className: 'flex flex-wrap gap-1 mt-1' },
                          breakdown.map(option =>
                            React.createElement(
                              'span',
                              {
                                key: option.name,
                                className:
                                  'inline-flex items-center gap-1 text-[10px] font-semibold text-panda-dark bg-panda-tint border border-panda-soft/60 rounded-full px-2 py-0.5',
                              },
                              React.createElement('span', null, option.name),
                              React.createElement(
                                'span',
                                { className: 'text-[#E53935]' },
                                '×',
                                option.quantity,
                              ),
                            ),
                          ),
                        ),
                      item.recipientName &&
                        React.createElement(
                          'span',
                          {
                            className:
                              'inline-flex items-center gap-1 text-[11px] text-gray-500 mt-1',
                          },
                          React.createElement(UserRound, {
                            className: 'w-3 h-3 text-[#E53935]',
                          }),
                          'For ',
                          React.createElement(
                            'span',
                            { className: 'font-semibold text-gray-700' },
                            item.recipientName,
                          ),
                        ),
                      item.specialInstructions &&
                        React.createElement(
                          'p',
                          {
                            className:
                              'text-[11px] text-gray-500 italic mt-0.5 line-clamp-2',
                          },
                          '"',
                          item.specialInstructions,
                          '"',
                        ),
                      React.createElement(
                        'span',
                        {
                          className:
                            'text-xs font-bold text-[#E53935] mt-1 block',
                        },
                        '$',
                        (item.price * item.quantity).toFixed(2),
                      ),
                    ),
                  ),
                  React.createElement(
                    'div',
                    { className: 'flex items-center gap-1.5 flex-shrink-0' },
                    onEditItem &&
                      React.createElement(
                        'button',
                        {
                          onClick: () => onEditItem(item),
                          className:
                            'w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 bg-gray-50 border border-gray-200 hover:bg-white hover:text-[#E53935] hover:border-[#E53935]/40 transition-colors',
                          title: 'Edit item',
                          'aria-label': `Edit ${item.name}`,
                        },
                        React.createElement(Pencil, { className: 'w-3 h-3' }),
                      ),
                    isMultiTier
                      ? React.createElement(
                          'button',
                          {
                            onClick: () => onRemoveItem(item.id),
                            className:
                              'w-7 h-7 rounded-lg flex items-center justify-center text-red-500 bg-gray-50 border border-gray-200 hover:bg-white transition-colors',
                            title: 'Remove',
                            'aria-label': `Remove ${item.name}`,
                          },
                          React.createElement(Trash2, { className: 'w-3.5 h-3.5' }),
                        )
                      : React.createElement(
                          'div',
                          {
                            className:
                              'flex items-center gap-1 bg-gray-50 rounded-lg p-1 border border-gray-200',
                          },
                          React.createElement(
                            'button',
                            {
                              onClick: () =>
                                onUpdateQuantity(item.id, item.quantity - 1),
                              className:
                                'w-6 h-6 rounded flex items-center justify-center text-gray-600 hover:bg-white hover:text-[#E53935] transition-colors',
                              title: 'Decrease',
                            },
                            item.quantity === 1
                              ? React.createElement(Trash2, {
                                  className: 'w-3 h-3 text-red-500',
                                })
                              : React.createElement(Minus, { className: 'w-3 h-3' }),
                          ),
                          React.createElement(
                            'span',
                            {
                              className:
                                'text-xs font-bold w-4 text-center text-[#202124]',
                            },
                            item.quantity,
                          ),
                          React.createElement(
                            'button',
                            {
                              onClick: () =>
                                onUpdateQuantity(item.id, item.quantity + 1),
                              className:
                                'w-6 h-6 rounded flex items-center justify-center text-gray-600 hover:bg-white hover:text-[#E53935] transition-colors',
                              title: 'Increase',
                            },
                            React.createElement(Plus, { className: 'w-3 h-3' }),
                          ),
                        ),
                  ),
                ),
              );
            }),
          ),
    ),
    items.length > 0 &&
      React.createElement(
        'div',
        {
          className:
            'p-4 sm:p-5 border-t border-gray-100 bg-panda-tint/30 space-y-3',
        },
        React.createElement(
          'div',
          { className: 'space-y-1.5 text-xs text-gray-600' },
          React.createElement(
            'div',
            { className: 'flex justify-between' },
            React.createElement('span', null, 'Subtotal'),
            React.createElement(
              'span',
              { className: 'font-semibold text-[#202124]' },
              '$',
              subtotal.toFixed(2),
            ),
          ),
          React.createElement(
            'div',
            { className: 'flex justify-between items-center' },
            React.createElement(
              'span',
              { className: 'flex items-center gap-1' },
              'Delivery ',
              React.createElement(
                'span',
                { className: 'text-[10px] text-gray-400' },
                '\u24D8',
              ),
            ),
            React.createElement(
              'span',
              { className: 'font-semibold text-[#202124]' },
              deliveryFee === 0
                ? React.createElement(
                    'span',
                    { className: 'text-[#65A30D] font-bold' },
                    'FREE',
                  )
                : `$${deliveryFee.toFixed(2)}`,
            ),
          ),
          discountAmount > 0 &&
            React.createElement(
              'div',
              { className: 'flex justify-between text-[#65A30D] font-medium' },
              React.createElement('span', null, 'Panda Discount'),
              React.createElement(
                'span',
                null,
                '-$',
                discountAmount.toFixed(2),
              ),
            ),
          React.createElement(
            'div',
            { className: 'flex justify-between' },
            React.createElement(
              'span',
              null,
              `Sales Tax (${(taxRate * 100).toFixed(3)}%)`,
            ),
            React.createElement(
              'span',
              { className: 'font-semibold text-[#202124]' },
              '$',
              tax.toFixed(2),
            ),
          ),
          React.createElement(
            'div',
            {
              className:
                'pt-2 border-t border-gray-200 flex justify-between items-center text-base font-bold text-[#202124]',
            },
            React.createElement('span', null, 'Total'),
            React.createElement(
              'span',
              { className: 'text-[#E53935] font-brand text-xl' },
              '$',
              total.toFixed(2),
            ),
          ),
        ),
        React.createElement(
          'div',
          { className: 'pt-1' },
          React.createElement(
            'div',
            { className: 'flex gap-2' },
            React.createElement(
              'div',
              { className: 'relative flex-1' },
              React.createElement(Tag, {
                className:
                  'w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400',
              }),
              React.createElement('input', {
                type: 'text',
                placeholder: 'Enter promo code (e.g. PANDA10)',
                value: promoInput,
                onChange: e => setPromoInput(e.target.value),
                className:
                  'w-full pl-8 pr-2 py-2 text-xs bg-white rounded-xl border border-gray-200 text-[#202124] focus:outline-none focus:ring-1 focus:ring-[#E53935]',
              }),
            ),
            React.createElement(
              'button',
              {
                onClick: handleApplyPromo,
                className:
                  'px-4 py-2 rounded-xl bg-panda-tint hover:bg-panda-tint text-[#E53935] font-bold text-xs transition-colors',
              },
              'Apply',
            ),
          ),
          promoMessage &&
            React.createElement(
              'p',
              {
                className: `text-[11px] mt-1 ${
                  promoMessage.type === 'success'
                    ? 'text-[#65A30D]'
                    : 'text-red-500'
                }`,
              },
              promoMessage.text,
            ),
        ),
        React.createElement(
          'button',
          {
            id: 'cart-checkout-btn',
            onClick: onCheckout,
            disabled,
            className:
              'w-full py-3.5 px-4 rounded-xl bg-[#E53935] hover:bg-[#d32f2f] text-white font-heading font-bold text-sm shadow-md shadow-red-500/20 active:scale-98 transition-all flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#E53935]',
          },
          React.createElement(
            'span',
            null,
            disabled ? 'Not Accepting Orders' : 'Proceed to Checkout',
          ),
          React.createElement(
            'div',
            { className: 'flex items-center gap-1.5 font-brand text-base' },
            React.createElement('span', null, '$', total.toFixed(2)),
            React.createElement(ArrowRight, { className: 'w-4 h-4' }),
          ),
        ),
        React.createElement(
          'div',
          { className: 'grid grid-cols-3 gap-2 pt-1' },
          React.createElement(
            'button',
            {
              onClick: handleShareCart,
              className:
                'py-2 px-1 rounded-xl bg-white border border-gray-200 hover:border-gray-300 text-[11px] font-semibold text-gray-700 flex items-center justify-center gap-1 transition-colors',
              title: 'Share Cart',
            },
            copiedShare
              ? React.createElement(Check, {
                  className: 'w-3 h-3 text-[#65A30D]',
                })
              : React.createElement(Share2, {
                  className: 'w-3 h-3 text-gray-500',
                }),
            React.createElement('span', null, copiedShare ? 'Copied' : 'Share'),
          ),
          React.createElement(
            'button',
            {
              onClick: onSaveForLater,
              disabled: isSavingForLater || isCreatingGroupOrder,
              className:
                'py-2 px-1 rounded-xl bg-white border border-gray-200 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-semibold text-gray-700 flex items-center justify-center gap-1 transition-colors',
              title: 'Save Cart for later',
            },
            isSavingForLater
              ? React.createElement('span', {
                  className:
                    'w-3 h-3 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin',
                })
              : React.createElement(Bookmark, {
                  className: 'w-3 h-3 text-gray-500',
                }),
            React.createElement(
              'span',
              null,
              isSavingForLater ? 'Saving...' : 'Save',
            ),
          ),
          React.createElement(
            'button',
            {
              onClick: onCreateGroupOrder,
              disabled: isSavingForLater || isCreatingGroupOrder || disabled,
              className:
                'py-2 px-1 rounded-xl bg-white border border-gray-200 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-semibold text-gray-700 flex items-center justify-center gap-1 transition-colors',
              title: 'Corporate order with friends',
            },
            isCreatingGroupOrder
              ? React.createElement('span', {
                  className:
                    'w-3 h-3 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin',
                })
              : React.createElement(Users, {
                  className: 'w-3 h-3 text-gray-500',
                }),
            React.createElement(
              'span',
              null,
              isCreatingGroupOrder ? 'Creating...' : 'Group',
            ),
          ),
        ),
      ),
  );
  if (isDrawer) {
    if (!isOpen) return null;
    return React.createElement(
      'div',
      {
        className:
          'fixed inset-0 z-50 bg-[#202124]/60 backdrop-blur-xs flex justify-end',
      },
      React.createElement(
        'div',
        {
          className:
            'w-full max-w-md h-full bg-white shadow-2xl animate-in slide-in-from-right duration-200',
        },
        content,
      ),
    );
  }
  return content;
};
